import { useState } from "react";
import { Link } from "react-router-dom";
import { PHONE } from "@/mocks/updiag";
import { SHOW_REVIEWS } from "@/config/features";

const steps = ["Type de bien", "Votre bien", "Contact"];

const typeOptions = [
  { value: "appartement", label: "Appartement", icon: "ri-building-2-line" },
  { value: "maison", label: "Maison", icon: "ri-home-2-line" },
  { value: "local", label: "Local commercial", icon: "ri-store-2-line" },
];

const diagnosticOptions = [
  { value: "dpe", label: "DPE" },
  { value: "pack-vente", label: "Pack Vente" },
  { value: "pack-location", label: "Pack Location" },
  { value: "amiante", label: "Amiante" },
  { value: "plomb", label: "Plomb (CREP)" },
  { value: "electricite", label: "Électricité" },
  { value: "gaz", label: "Gaz" },
  { value: "termites", label: "Termites" },
  { value: "loi-carrez", label: "Loi Carrez" },
];

export default function HeroSection() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    type: "",
    diagnostic: "",
    surface: "",
    annee: "",
    nom: "",
    email: "",
    telephone: "",
    ville: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch("https://readdy.ai/api/form/d7evamqa509fjl5l00i0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-[680px] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="/img/readdy/hero1.jpg"
          alt="Diagnostic immobilier Paris"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F3B5F]/90 via-[#0F3B5F]/70 to-[#0F3B5F]/30"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            {/* Badge */}
            {SHOW_REVIEWS ? (
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-6">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-[#F59E0B] text-sm"></i>
                  ))}
                </div>
                <span className="text-white text-sm font-semibold">4.9/5 — 127 avis Google</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-6">
                <i className="ri-award-fill text-[#F59E0B] text-sm"></i>
                <span className="text-white text-sm font-semibold">Certifié Cofrac · RDV sous 48h</span>
              </div>
            )}

            <h1 className="text-4xl md:text-5xl lg:text-[52px] font-extrabold text-white leading-tight mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
              Diagnostic immobilier<br />
              à Paris &amp; Île-de-France<br />
              <span className="text-[#F97316]">DPE, Amiante, Plomb</span>
            </h1>

            <p className="text-lg text-white/85 mb-8 max-w-lg leading-relaxed">
              Expert certifié COFRAC, nous réalisons tous vos diagnostics obligatoires pour la vente ou la location. Rapport le jour même, tarifs transparents.
            </p>

            {/* 4 points clés */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: "ri-award-fill", text: "Certifié COFRAC", color: "text-[#F97316]" },
                { icon: "ri-timer-flash-fill", text: "Devis en 2 minutes", color: "text-[#16A34A]" },
                { icon: "ri-price-tag-3-fill", text: "Dès 130 € TTC", color: "text-[#F59E0B]" },
                { icon: "ri-map-pin-2-fill", text: "Toute l'Île-de-France", color: "text-[#F97316]" },
              ].map((pt) => (
                <div key={pt.text} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3">
                  <i className={`${pt.icon} ${pt.color} text-lg`}></i>
                  <span className="text-white text-sm font-medium">{pt.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/devis"
                className="flex items-center justify-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold text-base hover:bg-orange-600 transition-all duration-200 shadow-lg whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Obtenir mon devis gratuit
              </Link>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-2 px-7 py-4 bg-white/15 backdrop-blur-sm border-2 border-white/50 text-white rounded-xl font-bold text-base hover:bg-white/25 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                Appeler maintenant
              </a>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-8">
              {[
                { value: "+2 500", label: "diagnostics réalisés" },
                { value: "48h", label: "délai d'intervention" },
                { value: "10 ans", label: "d'expertise" },
              ].map((stat, i) => (
                <div key={stat.label} className="flex items-center gap-4">
                  {i > 0 && <div className="w-px h-10 bg-white/30"></div>}
                  <div>
                    <div className="text-2xl font-extrabold text-white" style={{ fontFamily: "Barlow, sans-serif" }}>{stat.value}</div>
                    <div className="text-xs text-white/70">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Formulaire */}
          <div className="flex justify-center lg:justify-end">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>Demande envoyée !</h3>
                  <p className="text-gray-500 text-sm">Nous vous recontactons sous 2h avec votre devis personnalisé.</p>
                </div>
              ) : (
                <>
                  {/* Stepper */}
                  <div className="flex items-center justify-between mb-7">
                    {steps.map((s, i) => (
                      <div key={s} className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          i < step ? "bg-[#16A34A] text-white" : i === step ? "bg-[#F97316] text-white" : "bg-gray-100 text-gray-400"
                        }`}>
                          {i < step ? <i className="ri-check-line"></i> : i + 1}
                        </div>
                        <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-[#0F3B5F]" : "text-gray-400"}`}>{s}</span>
                        {i < steps.length - 1 && (
                          <div className={`flex-1 h-0.5 w-8 mx-1 ${i < step ? "bg-[#16A34A]" : "bg-gray-200"}`}></div>
                        )}
                      </div>
                    ))}
                  </div>

                  <form data-readdy-form onSubmit={handleSubmit}>
                    {/* Step 0 */}
                    {step === 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Type de bien</h3>
                        <p className="text-sm text-gray-500 mb-5">Quel type de bien souhaitez-vous faire diagnostiquer ?</p>
                        <div className="flex flex-col gap-3 mb-6">
                          {typeOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, type: opt.value })}
                              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                                formData.type === opt.value
                                  ? "border-[#F97316] bg-orange-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${formData.type === opt.value ? "bg-[#F97316] text-white" : "bg-gray-100 text-gray-500"}`}>
                                <i className={`${opt.icon} text-xl`}></i>
                              </div>
                              <span className="font-semibold text-[#0F3B5F]">{opt.label}</span>
                              {formData.type === opt.value && <i className="ri-checkbox-circle-fill text-[#F97316] ml-auto text-xl"></i>}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          disabled={!formData.type}
                          onClick={() => setStep(1)}
                          className="w-full py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          Continuer <i className="ri-arrow-right-line ml-1"></i>
                        </button>
                      </div>
                    )}

                    {/* Step 1 */}
                    {step === 1 && (
                      <div>
                        <h3 className="text-lg font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Votre bien</h3>
                        <p className="text-sm text-gray-500 mb-5">Quelques informations sur votre bien</p>
                        <div className="flex flex-col gap-4 mb-6">
                          <div>
                            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Diagnostic souhaité</label>
                            <select
                              name="diagnostic"
                              value={formData.diagnostic}
                              onChange={(e) => setFormData({ ...formData, diagnostic: e.target.value })}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                            >
                              <option value="">Sélectionner...</option>
                              {diagnosticOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Surface (m²)</label>
                              <input
                                type="number"
                                name="surface"
                                placeholder="Ex: 65"
                                value={formData.surface}
                                onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Année de construction</label>
                              <input
                                type="number"
                                name="annee"
                                placeholder="Ex: 1975"
                                value={formData.annee}
                                onChange={(e) => setFormData({ ...formData, annee: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Ville / Arrondissement</label>
                            <input
                              type="text"
                              name="ville"
                              placeholder="Ex: Paris 15e, Boulogne-Billancourt..."
                              value={formData.ville}
                              onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={() => setStep(0)} className="px-5 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:border-gray-300 transition-all whitespace-nowrap">
                            <i className="ri-arrow-left-line"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setStep(2)}
                            className="flex-1 py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all duration-200 whitespace-nowrap"
                          >
                            Continuer <i className="ri-arrow-right-line ml-1"></i>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 2 */}
                    {step === 2 && (
                      <div>
                        <h3 className="text-lg font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Vos coordonnées</h3>
                        <p className="text-sm text-gray-500 mb-5">Pour recevoir votre devis personnalisé</p>
                        <div className="flex flex-col gap-4 mb-6">
                          <div>
                            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Nom complet</label>
                            <input
                              type="text"
                              name="nom"
                              placeholder="Jean Dupont"
                              value={formData.nom}
                              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Email</label>
                            <input
                              type="email"
                              name="email"
                              placeholder="jean@exemple.fr"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Téléphone</label>
                            <input
                              type="tel"
                              name="telephone"
                              placeholder="06 XX XX XX XX"
                              value={formData.telephone}
                              onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={() => setStep(1)} className="px-5 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:border-gray-300 transition-all whitespace-nowrap">
                            <i className="ri-arrow-left-line"></i>
                          </button>
                          <button
                            type="submit"
                            disabled={submitting || !formData.nom || !formData.email}
                            className="flex-1 py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
                          >
                            {submitting ? "Envoi..." : "Obtenir mon devis gratuit"}
                            {!submitting && <i className="ri-arrow-right-line ml-1"></i>}
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-400 text-center mt-3">Réponse sous 2h — Aucun engagement</p>
                      </div>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
